import {
  IPaymentPlanTerms,
  IPortalInvoiceResponse,
  PaymentPlanPreview,
  paymentPlanUtils,
} from "shared";
import { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { QueueListIcon } from "@heroicons/react/24/outline";

import { CreatePaymentPlanModal } from "../../../common/Modals/CreatePaymentPlanModal";
import { useCreatePaymentPlanMutation } from "../../services/api/invoice";
import { env } from "../../../common/lib/env";
import { useSession } from "../../hooks/useSession";

import { InvoiceOption } from "./InvoiceOption";

interface ICreatePaymentPlanProps {
  invoiceData: IPortalInvoiceResponse;
}

export function canCreatePaymentPlan(invoiceData: IPortalInvoiceResponse) {
  return !!invoiceData.eligiblePaymentPlanConfig && !invoiceData.invoice?.currentPaymentPlan;
}

export function CreatePaymentPlan({
  invoiceData,
}: ICreatePaymentPlanProps) {
  const [createPaymentPlanModalOpen, setCreatePaymentPlanModalOpen] = useState(false);
  const [terms, setTerms] = useState<IPaymentPlanTerms>(paymentPlanUtils.createDefaultTermsForConfig(
    invoiceData.eligiblePaymentPlanConfig,
  ));
  const session = useSession();
  const [preview, setPreview] = useState<PaymentPlanPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [createPaymentPlan, { isLoading: createLoading }] = useCreatePaymentPlanMutation();

  if (!canCreatePaymentPlan(invoiceData)) {
    return null;
  }

  function setTermsAndClearPreview(next: IPaymentPlanTerms) {
    // Clear preview whenever options change
    if (preview) {
      setPreview(null);
    }
    if (previewError) {
      setPreviewError(null);
    }

    setTerms(next);
  }

  function onClose() {
    setCreatePaymentPlanModalOpen(false);
    setPreview(null);
    setPreviewError(null);
  }

  async function onPreview() {
    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const response = await axios<PaymentPlanPreview>({
        method: `POST`,
        url: `${env.basePortalApiUrl}invoices/code/${invoiceData.invoice.hash}/preview-payment-plan`,
        data: terms,
        withCredentials: true,
      });

      setPreview(response.data);
      setPreviewLoading(false);
    }
    catch (err) {
      console.error(err);
      setPreviewLoading(false);

      const message = (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === `object` && `message` in err.response.data)
        ? String(err.response.data.message)
        : `Could not fetch payment plan preview`;

      setPreviewError(message);
      toast.error(message);
    }
  }

  async function onCreate() {
    if (!session?.id) {
      return;
    }

    await createPaymentPlan({
      code: invoiceData.invoice.hash,
      terms,
      sessionId: session.id,
    }).unwrap();

    toast.success(`Payment plan created successfully`);
    onClose();
  }

  return (
    <>
      <InvoiceOption
        icon={ QueueListIcon }
        title={ `Create a payment plan` }
        description={ `This invoice is eligible to be split into a payment plan` }
        onClick={ () => setCreatePaymentPlanModalOpen(true) }
      />

      <CreatePaymentPlanModal
        mode={ `portal` }
        config={ invoiceData.eligiblePaymentPlanConfig }
        isOpen={ createPaymentPlanModalOpen }
        onClose={ onClose }
        terms={ terms }
        setTerms={ setTermsAndClearPreview }
        isLoading={ createLoading }
        onCreate={ onCreate }
        preview={ preview }
        previewError={ previewError }
        onPreview={ onPreview }
        previewLoading={ previewLoading }
      />
    </>
  );
}
